import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const WmpGuide = () => (
  <div className="min-h-screen bg-[#F8FAFC]" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
    <SEO
      title="What is a Waste Management Plan? | Complete Australian Guide"
      description="Everything you need to know about Waste Management Plans in Australia. Learn what a WMP is, when you need one, what it includes, and how to create one for your development application."
      keywords="what is a waste management plan, waste management plan Australia, WMP guide, waste management plan template, how to write a waste management plan, waste management plan for development application"
      canonicalPath="/what-is-a-waste-management-plan"
    />
    <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />

    {/* Nav */}
    <nav className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-white/70 border-b border-white/40 shadow-sm">
      <div className="max-w-4xl mx-auto px-6 flex items-center justify-between h-14">
        <Link to="/" className="text-lg font-bold tracking-tight text-[#0A2540]">PlanGen<span className="text-blue-600">Consulting</span></Link>
        <Link to="/register" className="px-5 py-1.5 rounded-full bg-[#0A2540] text-white text-sm font-medium hover:bg-black transition-colors">Get started free</Link>
      </div>
    </nav>

    <article className="max-w-4xl mx-auto px-6 pt-24 pb-16">
      <div className="flex items-center gap-2 text-xs text-slate-400 mb-8">
        <Link to="/" className="hover:text-blue-600">Home</Link>
        <span>/</span>
        <span className="text-slate-600">What is a Waste Management Plan?</span>
      </div>

      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 text-purple-700 text-xs font-semibold mb-4 border border-purple-200">
        <span>Complete Guide</span>
      </div>

      <h1 className="text-4xl lg:text-5xl font-black tracking-tighter text-[#0A2540] mb-6 leading-[1.1]">
        What is a Waste<br />Management Plan?
      </h1>

      <p className="text-lg text-slate-600 mb-10 leading-relaxed max-w-2xl">
        A Waste Management Plan (WMP) is a document that outlines how waste will be managed during the construction, demolition, and ongoing operation of a building or development. In Australia, it's typically a mandatory requirement for Development Applications (DAs) submitted to local councils.
      </p>

      {/* Quick answer */}
      <section className="mb-12">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h2 className="text-lg font-bold text-[#0A2540] mb-2">The Short Answer</h2>
          <p className="text-sm text-slate-700 leading-relaxed">
            A Waste Management Plan tells your council: <strong>"Here's how much waste this development will produce, where we'll store it, how often it gets collected, and what we'll do to minimise waste and maximise recycling."</strong> Without one, your DA will likely be rejected or require resubmission.
          </p>
        </div>
      </section>

      {/* Who needs one */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-[#0A2540] mb-4">Who Needs a Waste Management Plan?</h2>
        <p className="text-base text-slate-600 mb-4 leading-relaxed">
          If you're developing, building, or renovating property in Australia, you likely need a WMP. Specifically:
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { title: 'Residential Developments', desc: 'Multi-dwelling residential buildings (typically 3+ dwellings in Victoria, 5+ in some NSW councils). Apartments, townhouses, mixed-use with residential.' },
            { title: 'Commercial Developments', desc: 'Office buildings, retail premises, shopping centres, hotels, restaurants. Any commercial use generating ongoing waste.' },
            { title: 'Industrial Projects', desc: 'Warehouses, factories, logistics facilities. Often require additional EPA waste classification documentation.' },
            { title: 'Demolition & Construction', desc: 'Any project involving demolition of existing structures or significant construction. D&CWMP required to show material recovery targets.' },
          ].map(item => (
            <div key={item.title} className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-base font-semibold text-[#0A2540] mb-2">{item.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Types */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-[#0A2540] mb-4">Types of Waste Management Plans</h2>
        <p className="text-base text-slate-600 mb-4 leading-relaxed">
          There are three main types, and you may need one or more depending on your project:
        </p>

        <div className="space-y-4">
          <div className="bg-white rounded-xl border-l-4 border-emerald-500 border-r border-t border-b border-r-slate-200 border-t-slate-200 border-b-slate-200 p-6">
            <h3 className="text-lg font-bold text-[#0A2540] mb-1">Operational WMP (OpWMP)</h3>
            <p className="text-xs text-emerald-600 font-semibold mb-2">For ongoing building operations</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              Covers waste management after the building is occupied. Calculates how much waste residents or tenants will generate, what bins are needed, how often they're collected, and where they're stored. This is the most common type required for residential DAs.
            </p>
          </div>

          <div className="bg-white rounded-xl border-l-4 border-amber-500 border-r border-t border-b border-r-slate-200 border-t-slate-200 border-b-slate-200 p-6">
            <h3 className="text-lg font-bold text-[#0A2540] mb-1">Demolition &amp; Construction WMP (D&amp;CWMP)</h3>
            <p className="text-xs text-amber-600 font-semibold mb-2">For the build phase</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              Details how waste will be managed during demolition and construction. Includes material estimates (concrete, timber, metals, plasterboard), nominated recycling facilities, and diversion targets. Most councils expect 80%+ of construction waste to be recycled.
            </p>
          </div>

          <div className="bg-white rounded-xl border-l-4 border-blue-500 border-r border-t border-b border-r-slate-200 border-t-slate-200 border-b-slate-200 p-6">
            <h3 className="text-lg font-bold text-[#0A2540] mb-1">Construction Management Plan (CMP)</h3>
            <p className="text-xs text-blue-600 font-semibold mb-2">For active construction site management</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              Addresses practical site logistics during construction — waste storage locations, skip bin placement, vehicle access, dust and noise management, and neighbour notifications. Required alongside the D&amp;CWMP for larger projects.
            </p>
          </div>
        </div>
      </section>

      {/* What does a WMP contain */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-[#0A2540] mb-4">What Does a WMP Contain?</h2>
        <p className="text-base text-slate-600 mb-4 leading-relaxed">
          While specific requirements vary between councils and states, a standard Australian Waste Management Plan typically includes:
        </p>
        <div className="space-y-3">
          {[
            { num: '01', title: 'Project Details', desc: 'Site address, development type, number of dwellings/units, gross floor area, council jurisdiction.' },
            { num: '02', title: 'Waste Generation Estimates', desc: 'Calculated waste volumes per week based on council-specific generation rates. Broken down by waste stream: general waste, recycling, organics/FOGO, glass (VIC), and bulky waste.' },
            { num: '03', title: 'Bin Configuration', desc: 'Number, size (120L, 240L, 660L, 1100L), and type of bins required for each waste stream. Calculated to handle peak generation with appropriate collection frequency.' },
            { num: '04', title: 'Waste Storage Room Design', desc: 'Location, dimensions, ventilation, drainage, lighting, and access design for the bin storage area. Must accommodate all bins with clearance for loading and maneuvering.' },
            { num: '05', title: 'Collection Arrangements', desc: 'Private contractor or council collection, collection frequency (weekly, twice-weekly), collection point location, and any specific requirements for rear-lift, front-lift, or hook-lift vehicles.' },
            { num: '06', title: 'Vehicle Access', desc: 'Confirmation that waste collection vehicles (SRV 6.4m, MRV 8.8m, or HRV 12.5m) can access the collection point. Swept path analysis may be required for constrained sites.' },
            { num: '07', title: 'Waste Minimisation', desc: 'Strategies to reduce waste generation, increase recycling, and support circular economy principles. May include resident education programs, shared resource spaces, and compaction equipment.' },
          ].map(item => (
            <div key={item.num} className="flex gap-4 bg-white rounded-xl border border-slate-200 p-5">
              <div className="text-2xl font-black text-slate-200 flex-shrink-0 w-8">{item.num}</div>
              <div>
                <h3 className="text-base font-semibold text-[#0A2540] mb-1">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* State by state */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-[#0A2540] mb-4">WMP Requirements by State</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <Link to="/waste-management-plan-nsw" className="block bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
            <div className="text-2xl font-black text-[#0A2540] mb-1">NSW</div>
            <p className="text-sm text-slate-500 mb-3">33 councils covered. Council-specific DCP rates. DA, CDC, CC, SSDA applications.</p>
            <span className="text-sm font-medium text-blue-600">View NSW Guide &rarr;</span>
          </Link>
          <Link to="/waste-management-plan-vic" className="block bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
            <div className="text-2xl font-black text-[#0A2540] mb-1">Victoria</div>
            <p className="text-sm text-slate-500 mb-3">31 councils covered. FOGO mandatory. Better Apartment Design Standards.</p>
            <span className="text-sm font-medium text-blue-600">View VIC Guide &rarr;</span>
          </Link>
          <div className="bg-white rounded-xl border border-slate-200 p-6 opacity-70">
            <div className="text-2xl font-black text-[#0A2540] mb-1">Queensland</div>
            <p className="text-sm text-slate-500 mb-3">Council profiles coming soon. Contact us for consultant QLD plans.</p>
            <span className="text-xs text-slate-400">Coming soon</span>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6 opacity-70">
            <div className="text-2xl font-black text-[#0A2540] mb-1">Other States</div>
            <p className="text-sm text-slate-500 mb-3">WA, SA, TAS, ACT. Contact us for consultant plans in these jurisdictions.</p>
            <span className="text-xs text-slate-400">Coming soon</span>
          </div>
        </div>
      </section>

      {/* How PlanGen helps */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-[#0A2540] mb-4">How to Create a WMP with PlanGen</h2>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { step: '1', title: 'Enter Project Details', desc: 'Select your council, enter dwelling count and development type. Our system loads council-specific waste generation rates automatically.' },
              { step: '2', title: 'Review &amp; Customise', desc: 'The generator calculates bin configurations, collection schedules, and storage requirements. Adjust as needed for your specific project.' },
              { step: '3', title: 'Download PDF', desc: 'Export a professional, council-compliant WMP as a PDF document ready for inclusion in your DA submission pack.' },
            ].map(item => (
              <div key={item.step} className="text-center">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-lg font-bold text-blue-600">{item.step}</span>
                </div>
                <h3 className="text-sm font-semibold text-[#0A2540] mb-1">{item.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed" dangerouslySetInnerHTML={{ __html: item.desc }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional tools */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-[#0A2540] mb-4">More Than Just a Document Generator</h2>
        <p className="text-base text-slate-600 mb-4 leading-relaxed">
          PlanGen also offers interactive design tools to help you get the physical infrastructure right:
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-base font-semibold text-[#0A2540] mb-2">Waste Room Builder</h3>
            <p className="text-sm text-slate-500 leading-relaxed">Upload your floor plan and drag real equipment (bins, compactors, carousels, chutes from Elephants Foot, Wastech, NextGen) onto a 2D canvas. Check clearances and export a layout PDF.</p>
            <p className="text-xs text-emerald-600 font-medium mt-2">Free to use</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-base font-semibold text-[#0A2540] mb-2">Swept Path Planner</h3>
            <p className="text-sm text-slate-500 leading-relaxed">Check that waste collection trucks (SRV, MRV, HRV) can access your development. Upload your site plan, draw the vehicle path, and verify turning radii against AS 2890.2.</p>
            <p className="text-xs text-emerald-600 font-medium mt-2">Free to use</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A2540] rounded-2xl p-10 text-center mb-12">
        <h2 className="text-2xl font-bold text-white mb-3">Ready to Create Your WMP?</h2>
        <p className="text-slate-400 text-sm mb-6 max-w-md mx-auto">Free to sign up. Covers 64 councils across NSW and Victoria. Professional PDF export in under 30 minutes.</p>
        <div className="flex items-center justify-center gap-4">
          <Link to="/register" className="px-8 py-3 rounded-full bg-white text-[#0A2540] font-semibold text-sm hover:bg-slate-100 transition-colors">Get Started Free</Link>
          <Link to="/bespoke" className="px-8 py-3 rounded-full border border-white/20 text-white font-medium text-sm hover:bg-white/10 transition-colors">Consultant Plans</Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-[#0A2540] mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {[
            { q: 'How much does a Waste Management Plan cost?', a: 'Professional waste consultants typically charge $1,500 to $5,000+ for a WMP. Our online generator produces council-compliant plans at a fraction of the cost. For complex or bespoke projects, we also offer expert-written plans at competitive rates.' },
            { q: 'Can I write my own Waste Management Plan?', a: 'Technically yes, but councils expect specific waste generation rates, correct bin calculations, and compliance with their DCP requirements. Our generator handles all of this automatically, ensuring your plan meets council standards.' },
            { q: 'What happens if I submit a DA without a WMP?', a: 'Your DA will likely be returned as incomplete or rejected outright. Most Australian councils now consider a WMP a mandatory component of a complete DA submission for developments above their threshold.' },
            { q: 'How often does a WMP need to be updated?', a: 'Operational WMPs should be reviewed when there are significant changes to the building\'s use, tenant mix, or waste collection arrangements. Construction WMPs are project-specific and apply for the duration of the build.' },
            { q: 'What is the difference between a WMP and a waste audit?', a: 'A WMP is a forward-looking plan that estimates future waste generation for a proposed development. A waste audit is a retrospective assessment of actual waste being generated by an existing building or operation.' },
          ].map((faq, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-base font-semibold text-[#0A2540] mb-2">{faq.q}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "How much does a Waste Management Plan cost?", "acceptedAnswer": { "@type": "Answer", "text": "Professional waste consultants typically charge $1,500 to $5,000+. Our online generator produces council-compliant plans at a fraction of the cost." }},
          { "@type": "Question", "name": "Can I write my own Waste Management Plan?", "acceptedAnswer": { "@type": "Answer", "text": "Technically yes, but councils expect specific waste generation rates and DCP compliance. Our generator handles this automatically." }},
          { "@type": "Question", "name": "What happens if I submit a DA without a WMP?", "acceptedAnswer": { "@type": "Answer", "text": "Your DA will likely be returned as incomplete or rejected. Most Australian councils consider a WMP mandatory for developments above their threshold." }},
          { "@type": "Question", "name": "How often does a WMP need to be updated?", "acceptedAnswer": { "@type": "Answer", "text": "Operational WMPs should be reviewed when there are significant changes to the building's use or waste collection arrangements." }},
          { "@type": "Question", "name": "What is the difference between a WMP and a waste audit?", "acceptedAnswer": { "@type": "Answer", "text": "A WMP estimates future waste for a proposed development. A waste audit assesses actual waste from an existing building." }},
        ]
      })}} />
    </article>

    <footer className="bg-[#0A2540] py-8">
      <div className="max-w-4xl mx-auto px-6 text-center text-xs text-slate-500">
        <p>&copy; {new Date().getFullYear()} PlanGen Consulting. Professional Indemnity &amp; Public Liability Insured.</p>
        <div className="mt-2">
          <Link to="/" className="text-slate-400 hover:text-white mr-4">Home</Link>
          <Link to="/waste-management-plan-nsw" className="text-slate-400 hover:text-white mr-4">WMP NSW</Link>
          <Link to="/waste-management-plan-vic" className="text-slate-400 hover:text-white">WMP Victoria</Link>
        </div>
      </div>
    </footer>
  </div>
);

export default WmpGuide;
